import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "../lib/api";
import { Drawer, Field, StatusBadge } from "../components/UI";
import { Page } from "../components/AppShell";
import ListControls, { STATUS_OPTS } from "../components/ListControls";
import { Plus, EnvelopeSimple } from "@phosphor-icons/react";
import useBulkSelect from "../lib/useBulkSelect";

export default function Tasks() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, page_size: 20 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assignee_id: "", priority: "medium", status: "todo", due_date: "" });
  const [editingId, setEditingId] = useState(null);

  const load = () => {
    const params = new URLSearchParams({ page: String(page), page_size: "20" });
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    if (priority) params.set("priority", priority);
    // iter 73.7 — RLS hardening: coalesce missing keys so `data.items.length`
    // / `data.items.map(...)` never throws if a future RLS-enforced backend
    // role returns a partial / empty body for tenants with no tasks.
    api.get(`/tasks?${params}`).then(r => setData({ items: [], total: 0, ...(r.data || {}) }));
  };
  useEffect(() => { api.get("/users").then(r => setUsers(r.data || [])); }, []);
  useEffect(() => { load(); }, [page, status, priority]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 300); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const submit = async () => {
    const payload = { ...form, assignee_id: form.assignee_id || null, due_date: form.due_date ? new Date(form.due_date).toISOString() : null };
    if (editingId) await api.put(`/tasks/${editingId}`, payload);
    else await api.post("/tasks", payload);
    setOpen(false); setEditingId(null);
    setForm({ title: "", description: "", assignee_id: "", priority: "medium", status: "todo", due_date: "" });
    load();
  };
  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      title: t.title || "", description: t.description || "",
      assignee_id: t.assignee_id || "", priority: t.priority || "medium",
      status: t.status || "todo",
      due_date: t.due_date ? t.due_date.slice(0, 10) : "",
    });
    setOpen(true);
  };
  const updateStatus = async (t, status) => { await api.put(`/tasks/${t.id}`, { ...t, status, due_date: t.due_date }); load(); };
  const uName = (id) => users.find(u => u.id === id)?.name || "—";
  const bulk = useBulkSelect(data.items, { testid: "task-row" });

  return (
    <Page
      title="Tasks"
      subtitle="OPERATIONS / TASKS"
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "Tasks"]}
      actions={<button className="btn btn-primary" onClick={() => setOpen(true)} data-testid="new-task-btn"><Plus size={14} /> NEW TASK</button>}
      testid="tasks-page"
    >
      <ListControls
        testid="tasks-controls"
        q={q} onQ={setQ} placeholder="Search title or description..."
        filters={[
          { key: "status", label: "STATUS", value: status, onChange: setStatus, options: STATUS_OPTS.task },
          { key: "priority", label: "PRIORITY", value: priority, onChange: setPriority, options: STATUS_OPTS.priority },
        ]}
        pagination={data} total={data.total} onPageChange={setPage}
      />
      <div className="surface"><table className="dense">
        <thead><tr>
          <th className="w-8"><input {...bulk.allProps()} /></th>
          <th>TITLE</th><th>ASSIGNEE</th><th>PRIORITY</th><th>STATUS</th><th>DUE</th>
        </tr></thead>
        <tbody>
          {data.items.length === 0 && <tr><td colSpan="6" className="text-center text-muted-foreground py-12">No tasks</td></tr>}
          {data.items.map(t => (
            <tr key={t.id} data-testid={`task-row-${t.id}`}>
              <td><input {...bulk.rowProps(t.id)} /></td>
              <td><div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <button className="link-cell font-semibold" onClick={() => openEdit(t)} data-testid={`task-row-open-${t.id}`} title="Open detailed view">
                    {t.title}
                  </button>
                  <div className="text-xs text-muted-foreground truncate max-w-md">{t.description}</div>
                </div>
                {t.email_message_id && (
                  <button
                    className="badge badge-default flex items-center gap-1 cursor-pointer hover:bg-secondary whitespace-nowrap"
                    title="Open the source email in the Unified Inbox"
                    onClick={() => navigate(`/email?open_account=${t.email_account_id}&open_msg=${t.email_message_id}`)}
                    data-testid={`linked-email-badge-${t.id}`}
                  >
                    <EnvelopeSimple size={11} /> LINKED EMAIL
                  </button>
                )}
              </div></td>
              <td>{uName(t.assignee_id)}</td>
              <td><StatusBadge status={t.priority} /></td>
              <td><select className="badge badge-default border-0 bg-transparent" value={t.status} onChange={e => updateStatus(t, e.target.value)}>
                {STATUS_OPTS.task.map(s => <option key={s}>{s}</option>)}
              </select></td>
              <td className="text-xs">{fmtDate(t.due_date)}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
      <Drawer open={open} onClose={() => { setOpen(false); setEditingId(null); }} title={editingId ? "Edit task" : "New task"} size="narrow" testid="new-task-drawer"
        footer={<><button className="btn btn-secondary" onClick={() => { setOpen(false); setEditingId(null); }}>CANCEL</button><button className="btn btn-primary" onClick={submit} data-testid="save-task-btn">SAVE</button></>}>
        <Field label="TITLE"><input className="input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" /></Field>
        <Field label="DESCRIPTION"><textarea className="input" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="ASSIGNEE"><select className="input" value={form.assignee_id} onChange={e => setForm({ ...form, assignee_id: e.target.value })}><option value="">—</option>{users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</select></Field>
          <Field label="DUE DATE"><input className="input" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="PRIORITY"><select className="input" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>{STATUS_OPTS.priority.map(s=><option key={s}>{s}</option>)}</select></Field>
          <Field label="STATUS"><select className="input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>{STATUS_OPTS.task.map(s=><option key={s}>{s}</option>)}</select></Field>
        </div>
      </Drawer>
    </Page>
  );
}
